import { useCallback, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { isMarked, MARK_KINDS, toggleMark, type MarkKind } from '@/lib/forms/marks';

/**
 * Bold / Italic / Highlight over a long-text field the site renders with its marks
 * (`x-marks`, F-R16-04). The field stays a `<textarea>`: each button writes or removes the
 * markers around the selection (`lib/forms/marks.ts`), so copy, paste and undo keep working.
 *
 * - The edit goes through `execCommand('insertText')` where the browser has it, so one
 *   click is one step on the textarea's own undo stack, and through `setRangeText` plus an
 *   `input` event where it does not (jsdom) or where it would be cut by a `maxlength`.
 * - Ctrl/Cmd+B and Ctrl/Cmd+I in the textarea do what the buttons do.
 * - The buttons never take focus from the textarea on a click, so the selection they act on
 *   is still the editor's. From the keyboard the toolbar is one tab stop (WAI-ARIA toolbar):
 *   the arrow keys move between the buttons, mirrored in Arabic.
 * - Each button is `aria-pressed` while the selection carries its mark.
 */

const NONE: ReadonlySet<MarkKind> = new Set();

const SHORTCUT_KEYS: Partial<Record<string, MarkKind>> = { b: 'bold', i: 'italic' };

export interface Marks {
  active: ReadonlySet<MarkKind>;
  apply: (kind: MarkKind) => void;
  /** The textarea's handlers: the shortcuts, and the pressed state following the selection. */
  onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  onSelect: () => void;
}

export function useMarks(target: RefObject<HTMLTextAreaElement | null>, enabled: boolean): Marks {
  const [active, setActive] = useState<ReadonlySet<MarkKind>>(NONE);

  const refresh = useCallback(() => {
    const el = target.current;
    if (!el || !enabled) return;
    const next = new Set(MARK_KINDS.filter((kind) => isMarked(el.value, el.selectionStart, el.selectionEnd, kind)));
    setActive((prev) => (prev.size === next.size && [...next].every((k) => prev.has(k)) ? prev : next));
  }, [target, enabled]);

  const apply = useCallback(
    (kind: MarkKind) => {
      const el = target.current;
      if (!el || !enabled || el.disabled || el.readOnly) return;
      const before = el.value;
      const edit = toggleMark(before, el.selectionStart, el.selectionEnd, kind);
      if (!edit) return;
      const expected = before.slice(0, edit.from) + edit.insert + before.slice(edit.to);
      el.focus();
      el.setSelectionRange(edit.from, edit.to);
      const fits = el.maxLength < 0 || expected.length <= el.maxLength;
      let done = false;
      if (fits && typeof document.execCommand === 'function') {
        try {
          done = document.execCommand('insertText', false, edit.insert);
        } catch {
          done = false;
        }
      }
      if (!done || el.value !== expected) {
        el.setRangeText(expected, 0, el.value.length);
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }
      el.setSelectionRange(edit.selStart, edit.selEnd);
      refresh();
    },
    [target, enabled, refresh],
  );

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!enabled || !(event.ctrlKey || event.metaKey) || event.altKey || event.shiftKey) return;
      const kind = SHORTCUT_KEYS[event.key.toLowerCase()];
      if (!kind) return;
      event.preventDefault();
      apply(kind);
    },
    [enabled, apply],
  );

  return { active, apply, onKeyDown, onSelect: refresh };
}

const GLYPH: Record<MarkKind, { text: string; className: string }> = {
  bold: { text: 'B', className: 'font-bold' },
  italic: { text: 'I', className: 'italic' },
  highlight: { text: 'ab', className: 'rounded-[2px] bg-signal-amber-fill px-[3px] text-ink-graphite' },
};

function isMac(): boolean {
  return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

export function MarksToolbar({
  controls,
  marks,
  disabled,
}: {
  /** The textarea's id. */
  controls: string;
  marks: Marks;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [focus, setFocus] = useState(0);
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const mod = isMac() ? '⌘' : 'Ctrl';

  const move = (event: KeyboardEvent<HTMLDivElement>) => {
    const rtl = getComputedStyle(event.currentTarget).direction === 'rtl';
    const step = { ArrowRight: rtl ? -1 : 1, ArrowLeft: rtl ? 1 : -1 }[event.key];
    let next: number | undefined;
    if (step !== undefined) next = (focus + step + MARK_KINDS.length) % MARK_KINDS.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = MARK_KINDS.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setFocus(next);
    buttons.current[next]?.focus();
  };

  return (
    <div
      role="toolbar"
      aria-label={t('forms.marks.toolbar')}
      aria-controls={controls}
      onKeyDown={move}
      className="inline-flex self-start overflow-hidden rounded-sm border-[0.5px] border-hairline-strong"
    >
      {MARK_KINDS.map((kind, i) => {
        const shortcut = kind === 'highlight' ? undefined : kind === 'bold' ? 'B' : 'I';
        const label = t(`forms.marks.${kind}`);
        return (
          <button
            key={kind}
            ref={(el) => {
              buttons.current[i] = el;
            }}
            type="button"
            tabIndex={i === focus ? 0 : -1}
            aria-pressed={marks.active.has(kind)}
            aria-label={label}
            aria-keyshortcuts={shortcut ? `Control+${shortcut} Meta+${shortcut}` : undefined}
            title={shortcut ? t('forms.marks.withShortcut', { label, shortcut: `${mod}+${shortcut}` }) : label}
            disabled={disabled}
            // The arrows step from the button that has focus, however it got there.
            onFocus={() => setFocus(i)}
            // Keep the textarea's focus and selection: the button acts on them.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setFocus(i);
              marks.apply(kind);
            }}
            className={cn(
              'inline-flex h-[32px] min-w-[36px] items-center justify-center px-sm font-sans text-small',
              'transition-colors duration-almanac focus-visible:relative focus-visible:z-1 focus-visible:shadow-focus',
              'disabled:cursor-not-allowed disabled:opacity-40',
              i > 0 && 'border-s-[0.5px] border-hairline',
              marks.active.has(kind)
                ? 'bg-canvas-deep-sand text-ink-graphite'
                : 'bg-canvas-paper text-ink-mid hover:bg-canvas-bone',
            )}
          >
            <span aria-hidden className={GLYPH[kind].className}>
              {GLYPH[kind].text}
            </span>
          </button>
        );
      })}
    </div>
  );
}
